<script>
/**
 * Ficha do cadastro de profissional (modal "Visualizar" em `list.vue`).
 * Recebe `currentProfessional` com `id` preenchido. A agenda do profissional logado
 * (`index_logged.vue` → `professional_calendar.vue`) não usa este componente no clique
 * do evento; o popup de evolução usa `responsible/detail.vue` (AgendaEventDetail).
 */
import MedicalRecordsPanel from "@/views/pages/medical-records/MedicalRecordsPanel.vue";
import { isProfessionalActive, professionalStatusLabel } from "@/helpers/professionalStatus";

export default {
  name: "ProfessionalDetail",
  components: { MedicalRecordsPanel },
  props: {
    currentProfessional: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      paymentDocumentLabels: {
        1: "Nota fiscal",
        2: "RPA",
      },
    };
  },
  computed: {
    prof() {
      return this.currentProfessional && this.currentProfessional.id
        ? this.currentProfessional
        : null;
    },
    initials() {
      if (!this.prof || !this.prof.name) return "?";
      const parts = String(this.prof.name).trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return this.prof.name.substring(0, 2).toUpperCase();
    },
    agendaColor() {
      const c = this.prof && this.prof.color;
      if (!c || String(c).trim() === "") return null;
      return String(c).startsWith("#") ? c : `#${c}`;
    },
    paymentDocumentLabel() {
      if (!this.prof || this.prof.payment_document == null) return null;
      return (
        this.paymentDocumentLabels[this.prof.payment_document] ||
        `Código ${this.prof.payment_document}`
      );
    },
    specialtyList() {
      if (!this.prof || !Array.isArray(this.prof.specialty)) return [];
      return this.prof.specialty;
    },
    statusLabel() {
      return professionalStatusLabel(this.prof);
    },
    isActive() {
      return isProfessionalActive(this.prof);
    },
  },
  methods: {
    display(v) {
      if (v === null || v === undefined || v === "") return "—";
      return v;
    },
    copyText(text) {
      if (!text || !navigator.clipboard) return;
      navigator.clipboard.writeText(String(text)).then(() => {
        this.$bvToast.toast("Copiado para a área de transferência.", {
          title: "",
          variant: "success",
          solid: true,
          autoHideDelay: 2000,
          toaster: "b-toaster-bottom-right",
        });
      });
    },
  },
};
</script>

<template>
  <div class="professional-detail">
    <div v-if="!prof" class="empty-state text-center py-4">
      <i class="ri-user-unfollow-line empty-state__icon"></i>
      <p class="text-muted mb-0 mt-2">
        Nenhum profissional carregado. Na listagem administrativa, use Visualizar no menu da
        tabela.
      </p>
    </div>

    <div v-else class="detail-layout">
      <!-- Cabeçalho -->
      <div class="detail-hero">
        <div
          class="detail-avatar"
          :style="agendaColor ? { background: agendaColor, color: '#fff' } : {}"
        >
          {{ initials }}
        </div>
        <div class="detail-hero__body">
          <div class="detail-hero__title-row">
            <h4 class="detail-name mb-1">{{ prof.name }}</h4>
            <b-badge variant="light" class="detail-id-badge">ID {{ prof.id }}</b-badge>
            <b-badge :variant="isActive ? 'success' : 'secondary'" class="ml-2">
              {{ statusLabel }}
            </b-badge>
          </div>
          <p class="detail-subtitle mb-0 text-muted">
            <i class="ri-stethoscope-line mr-1"></i>
            Profissional de saúde
            <span v-if="specialtyList.length" class="mx-1">·</span>
            <span v-if="specialtyList.length">
              {{ specialtyList.length }}
              especialidade(s)
            </span>
          </p>
        </div>
      </div>

      <b-tabs pills content-class="mt-3">
        <b-tab title="Dados gerais">
      <div class="row detail-grid">
        <!-- Contato -->
        <div class="col-md-6 mb-3">
          <div class="info-card h-100">
            <div class="info-card__head">
              <i class="ri-contacts-line info-card__icon"></i>
              <span>Contato</span>
            </div>
            <ul class="info-list list-unstyled mb-0">
              <li class="info-row">
                <span class="info-label">E-mail</span>
                <div class="info-value-wrap">
                  <a
                    v-if="prof.email"
                    :href="'mailto:' + prof.email"
                    class="info-link"
                  >{{ prof.email }}</a>
                  <span v-else class="text-muted">—</span>
                  <button
                    v-if="prof.email"
                    type="button"
                    class="btn-copy"
                    title="Copiar e-mail"
                    @click="copyText(prof.email)"
                  >
                    <i class="ri-file-copy-line"></i>
                  </button>
                </div>
              </li>
              <li class="info-row">
                <span class="info-label">Celular / WhatsApp</span>
                <div class="info-value-wrap">
                  <span>{{ display(prof.contact) }}</span>
                  <button
                    v-if="prof.contact"
                    type="button"
                    class="btn-copy"
                    title="Copiar telefone"
                    @click="copyText(prof.contact)"
                  >
                    <i class="ri-file-copy-line"></i>
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <!-- Registro profissional -->
        <div class="col-md-6 mb-3">
          <div class="info-card h-100">
            <div class="info-card__head">
              <i class="ri-government-line info-card__icon"></i>
              <span>Registro profissional</span>
            </div>
            <ul class="info-list list-unstyled mb-0">
              <li class="info-row">
                <span class="info-label">Conselho</span>
                <span class="info-value">{{ display(prof.council) }}</span>
              </li>
              <li class="info-row">
                <span class="info-label">Credencial / Registro</span>
                <span class="info-value font-monospace">{{ display(prof.credential) }}</span>
              </li>
              <li v-if="paymentDocumentLabel" class="info-row">
                <span class="info-label">Documento de pagamento</span>
                <b-badge variant="secondary" class="font-weight-normal">{{
                  paymentDocumentLabel
                }}</b-badge>
              </li>
            </ul>
          </div>
        </div>

        <!-- Especialidades -->
        <div class="col-12 mb-3">
          <div class="info-card">
            <div class="info-card__head">
              <i class="ri-medicine-bottle-line info-card__icon"></i>
              <span>Especialidades / tratamentos</span>
            </div>
            <div v-if="specialtyList.length" class="specialty-tags">
              <b-badge
                v-for="s in specialtyList"
                :key="s.id"
                variant="primary"
                class="specialty-badge mr-2 mb-2"
              >{{ s.name }}</b-badge>
            </div>
            <p v-else class="text-muted mb-0 small">Nenhuma especialidade cadastrada.</p>
          </div>
        </div>

        <!-- Sistema -->
        <div class="col-12">
          <div class="info-card info-card--muted">
            <div class="info-card__head">
              <i class="ri-settings-3-line info-card__icon"></i>
              <span>Vínculos no sistema</span>
            </div>
            <div class="row system-row">
              <div class="col-sm-6 mb-2 mb-sm-0">
                <span class="info-label d-block mb-1">Usuário associado</span>
                <template v-if="prof.associated_user_id">
                  <b-badge variant="success" class="mr-2">
                    <i class="ri-links-line mr-1"></i>
                    Vinculado
                  </b-badge>
                  <span class="text-muted small">ID usuário: {{ prof.associated_user_id }}</span>
                </template>
                <b-badge v-else variant="warning">Sem usuário vinculado</b-badge>
              </div>
              <div class="col-sm-6 mb-2 mb-sm-0">
                <span class="info-label d-block mb-1">IDs técnicos</span>
                <div class="small text-muted">
                  <div>
                    Atendimento ID:
                    <code class="small">{{ display(prof.atendimento_id) }}</code>
                  </div>
                  <div>
                    Agenda ID:
                    <code class="small">{{ display(prof.agenda_id) }}</code>
                  </div>
                </div>
              </div>
              <div v-if="agendaColor" class="col-sm-6">
                <span class="info-label d-block mb-1">Cor na agenda</span>
                <div class="d-flex align-items-center">
                  <span
                    class="color-swatch mr-2"
                    :style="{ background: agendaColor }"
                  ></span>
                  <code class="small mb-0">{{ agendaColor }}</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </b-tab>

        <b-tab :title="$t('menuitems.prontuarios.text')" lazy>
          <MedicalRecordsPanel entity-type="professional" :entity-id="prof.id" />
        </b-tab>
      </b-tabs>
    </div>
  </div>
</template>

<style scoped>
.professional-detail {
  font-size: 0.9375rem;
}

.empty-state__icon {
  font-size: 3rem;
  color: #ced4da;
}

.detail-hero {
  display: flex;
  align-items: flex-start;
  gap: 1.25rem;
  padding: 1.25rem 1rem;
  margin: -0.5rem -0.25rem 1.25rem;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.detail-avatar {
  flex-shrink: 0;
  width: 72px;
  height: 72px;
  border-radius: 16px;
  background: linear-gradient(145deg, #556ee6 0%, #405189 100%);
  color: #fff;
  font-size: 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(85, 110, 230, 0.35);
  letter-spacing: -0.02em;
}

.detail-hero__body {
  min-width: 0;
  flex: 1;
}

.detail-hero__title-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
}

.detail-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
  line-height: 1.3;
}

.detail-id-badge {
  font-weight: 500;
  color: #495057 !important;
  border: 1px solid #dee2e6;
}

.detail-subtitle {
  font-size: 0.875rem;
}

.info-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem 1.125rem;
  transition: box-shadow 0.2s ease;
}

.info-card:hover {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.info-card--muted {
  background: #fafbfc;
}

.info-card__head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f5;
}

.info-card__icon {
  font-size: 1.125rem;
  color: #556ee6;
}

.info-list {
  margin: 0;
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.info-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.info-row:first-of-type {
  padding-top: 0;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #868e96;
}

.info-value,
.info-value-wrap {
  color: #212529;
  word-break: break-word;
}

.info-value-wrap {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.info-link {
  color: #556ee6;
  font-weight: 500;
}

.info-link:hover {
  color: #405189;
  text-decoration: none;
}

.btn-copy {
  border: none;
  background: #f1f3f5;
  color: #495057;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.btn-copy:hover {
  background: #556ee6;
  color: #fff;
}

.font-monospace {
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace;
  font-size: 0.9em;
}

.specialty-tags {
  display: flex;
  flex-wrap: wrap;
  margin: -0.25rem;
}

.specialty-badge {
  padding: 0.4em 0.75em;
  font-weight: 500;
  border-radius: 8px;
}

.system-row {
  margin-top: 0.25rem;
}

.color-swatch {
  display: inline-block;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #dee2e6, 0 2px 8px rgba(0, 0, 0, 0.08);
}
</style>
